import { useRef } from 'react';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Icon,
  Link,
  TableTd,
  TableTr,
  Text,
  useConfirmationModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useDeleteWebhook, useUpdateWebhook } from '@/services/db/webhook/mutations';
import { isForceDisabled, toWebhookWriteInput } from '@/services/db/webhook/utils';
import type { Webhook } from '@/services/db/webhook/entity';
import { isEmptyString } from '@/utils/string';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { useReportingWebhookAnalytics } from '../analytics';

const SIGNING_KEY_TOOLTIP = "Use this signing key to ensure the data you're receiving is from Customer.io.";
const NO_EVENTS_TOOLTIP = 'To enable, edit your webhook and select at least one event to send.';

type ReportingWebhookRowProps = {
  webhook: Webhook;
  /** The edit page for this webhook. */
  editHref: string;
  canEdit: boolean;
  canDelete: boolean;
  /** This is the only webhook currently enabled — drives the disable event's optional property. */
  isLastEnabled: boolean;
  /** This is the workspace's only webhook — drives the delete event's optional property. */
  isOnlyWebhook: boolean;
};

export function ReportingWebhookRow({
  webhook,
  editHref,
  canEdit,
  canDelete,
  isLastEnabled,
  isOnlyWebhook,
}: ReportingWebhookRowProps) {
  const snackbars = useSnackbars();
  const confirm = useConfirmationModal();
  const analytics = useReportingWebhookAnalytics();
  const updateWebhook = useUpdateWebhook();
  const deleteWebhook = useDeleteWebhook();

  // The Ember tasks are `drop: true` — a second activation while one is in flight is dropped, not
  // queued. A ref latch (rather than the mutation's isPending) is what makes that synchronous:
  // two clicks landing before the next render would both read a stale isPending.
  const isRunning = useRef(false);

  const runOnce = async (action: () => Promise<unknown>) => {
    if (isRunning.current) return;
    isRunning.current = true;
    try {
      await action();
    } finally {
      isRunning.current = false;
    }
  };

  const hasNoEvents = isForceDisabled(webhook.events);
  const displayName = isEmptyString(webhook.name) ? webhook.endpoint_url : webhook.name;

  const handleCopyKey = async () => {
    const copied = await copyToClipboard(webhook.signing_key);
    if (!copied) return;

    snackbars.create({
      id: 'integrations.reporting-webhooks.copy-key.success',
      message: "We've copied the webhook signing key to your clipboard.",
    });
    void analytics.trackKeyCopied(webhook.id);
  };

  const handleEnable = () =>
    runOnce(async () => {
      if (hasNoEvents) return;

      // Fires before the save, so an enable that then fails is still recorded — matching Ember.
      void analytics.trackEnableStarted();

      try {
        await updateWebhook.mutateAsync({
          webhookId: webhook.id,
          ...toWebhookWriteInput(webhook, { enabled: true }),
        });
      } catch {
        // Enable has no success snackbar in Ember — only this error one.
        snackbars.create({
          id: 'integrations.reporting-webhooks.enable.error',
          variant: 'critical',
          message: 'We ran into a problem trying to enable your webhook endpoint. Try again.',
        });
      }
    });

  const handleDisable = async () => {
    // Ember tracks when the modal opens, not on confirm.
    void analytics.trackDisableStarted(webhook.id, { isLastEnabled });

    const confirmed = await confirm({
      title: 'Disable this endpoint?',
      message: 'We will not send any events to your endpoint if the webhook is disabled.',
      cancelLabel: 'Cancel',
      confirmLabel: 'Continue to disable',
    });
    if (confirmed !== true) return;

    await runOnce(async () => {
      try {
        await updateWebhook.mutateAsync({
          webhookId: webhook.id,
          ...toWebhookWriteInput(webhook, { enabled: false }),
        });
        snackbars.create({
          id: 'integrations.reporting-webhooks.disable.success',
          message: 'Your webhook endpoint has been disabled.',
        });
      } catch {
        snackbars.create({
          id: 'integrations.reporting-webhooks.disable.error',
          variant: 'critical',
          message: 'We ran into a problem trying to disable your webhook endpoint. Try again.',
        });
      }
    });
  };

  const handleDelete = async () => {
    void analytics.trackDeleteStarted(webhook.id, { isOnlyWebhook });

    const confirmed = await confirm({
      title: 'Delete this endpoint?',
      message: 'Once deleted, you will no longer receive events to this endpoint. This cannot be undone.',
      cancelLabel: 'Cancel',
      confirmLabel: 'Delete endpoint',
      isCritical: true,
    });
    if (confirmed !== true) return;

    await runOnce(async () => {
      try {
        await deleteWebhook.mutateAsync({ webhookId: webhook.id });
        snackbars.create({
          id: 'integrations.reporting-webhooks.delete.success',
          message: 'Your webhook endpoint has been deleted.',
        });
      } catch {
        snackbars.create({
          id: 'integrations.reporting-webhooks.delete.error',
          variant: 'critical',
          message: 'We ran into a problem trying to delete your webhook endpoint. Try again.',
        });
      }
    });
  };

  return (
    <TableTr data-test-webhook={webhook.id}>
      <TableTd>
        <Text weight="semibold">
          <Link
            data-test-webhook-identifier
            href={editHref}
            variant="secondary"
            isUnderlined={false}
            icon="chevron-right"
            iconPosition="trailing"
            truncate
            onClick={() => {
              void analytics.trackEditStarted(webhook.id, 'Webhook Row Click');
            }}
          >
            {displayName}
          </Link>
        </Text>
      </TableTd>
      <TableTd textAlign="center">
        {/* Ember passed no color when disabled; `Badge` requires one alongside a status indicator,
            and grey is what the indicator rendered as. */}
        <Badge color={webhook.enabled ? 'green' : 'grey'} showStatusIndicator data-test-webhook-status={webhook.id}>
          {webhook.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </TableTd>
      <TableTd textAlign="right">
        <DropdownMenu>
          <DropdownMenuTriggerButton
            aria-label="Webhook actions"
            icon="more"
            isIconOnly
            variant="secondary"
            data-test-webhook-actions={webhook.id}
          />
          <DropdownMenuContent>
            <DropdownMenuItem
              href={editHref}
              isDisabled={!canEdit}
              onClick={() => {
                void analytics.trackEditStarted(webhook.id, 'Webhook Drop-down Click');
              }}
              data-test-webhook-action="edit"
            >
              Edit
            </DropdownMenuItem>

            {/* Copying the key is deliberately not permission-gated. */}
            <DropdownMenuItem
              tooltip={SIGNING_KEY_TOOLTIP}
              onClick={() => {
                void handleCopyKey();
              }}
              data-test-webhook-action="copy-key"
            >
              Copy signing key
              <Icon name="help" isInline inlineAlignment="lowercase" />
            </DropdownMenuItem>

            {webhook.enabled ? (
              <DropdownMenuItem
                isDisabled={!canEdit}
                onClick={() => {
                  void handleDisable();
                }}
                data-test-webhook-action="disable"
              >
                Disable
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                isDisabled={!canEdit || hasNoEvents}
                tooltip={hasNoEvents ? NO_EVENTS_TOOLTIP : undefined}
                onClick={() => {
                  void handleEnable();
                }}
                data-test-webhook-action="enable"
              >
                Enable
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              isDisabled={!canDelete}
              isCritical
              onClick={() => {
                void handleDelete();
              }}
              data-test-webhook-action="delete"
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableTd>
    </TableTr>
  );
}
